import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { twyneChainData } from '@1delta/data-sdk'
import { twyneLenderKey } from '../../public-data/twyne/convertPublic'
import { TWYNE_READS_PER_VAULT, getCachedTwyneDiscovery } from './userCallBuild'

/**
 * Per-position Twyne detail, attached to both rows of the position's market.
 *
 * Raw strings throughout: every write on a collateral vault is denominated in
 * the COLLATERAL ASSET (the receipt token), not in the underlying, so a caller
 * holding only the human number cannot build a transaction.
 */
export interface TwynePositionInfo {
  /** The collateral vault contract — this position's id. */
  collateralVault: string
  intermediateVault: string
  /** Collateral held by the vault INCLUDING reserved credit, receipt units. */
  totalAssetsDepositedOrReserved: string
  /** The reserved credit inside it (C_LP). NOT the borrower's, and NOT
   *  withdrawable — "withdraw all" is the difference, never the balance. */
  reservedCredit: string
  /** Borrower-owned collateral, receipt units: total − reserved. */
  userCollateral: string
  /** Debt to the EXTERNAL protocol, in target-asset units. */
  debt: string
  /** The position's CHOSEN liquidation LTV, 1e4. Settable at any time within
   *  the market's band — this is the value half of the parameter model. */
  twyneLiqLtv: string
  /** Liquidatable on TWYNE right now (by inheritance, not seize-and-sell). */
  canLiquidate: boolean
  /**
   * The EXTERNAL protocol has already liquidated this vault. In that state the
   * vault reverts EVERY function except `handleExternalLiquidation()`, so every
   * action builder must refuse up front rather than emit calldata that cannot
   * execute.
   */
  isExternallyLiquidated: boolean
  /** Excess reserved credit that `rebalance()` could release right now, receipt
   *  units. Permissionless, and it LOWERS what the borrower pays. `0` means
   *  none — the getter reverts in that case and we normalise it. */
  releasableCredit: string
  /** Receipt → underlying scale, per 1e18. Read, never assumed 1:1. */
  collateralToUnderlying: string
}

const WAD = 10n ** 18n

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/**
 * Returns a [converter, expectedCallCount] tuple for Twyne user data.
 *
 * Layout mirrors `buildTwyneUserCall`, driven by the SAME cached discovery —
 * re-discovering here could desync the slicer from the call list.
 *
 * Emits one `UserData` per market key, with one SUB-ACCOUNT per collateral
 * vault: a borrower may hold several positions in one market (the flagship
 * borrower holds four), each with its own collateral, debt and chosen LTV.
 *
 * Two modelling notes:
 *
 *  - **Deposits are the BORROWER-OWNED collateral**, i.e. total minus reserved
 *    credit, converted to the underlying. Publishing the total would credit the
 *    user with capital that belongs to the Credit-LPs and that they cannot
 *    withdraw.
 *  - **Nothing here is a health factor.** Twyne's threshold is per position and
 *    dynamically clamped, and liquidation is by inheritance — so the row
 *    carries `canLiquidate` (the protocol's own answer) rather than a ratio
 *    reconstructed from collateral and debt, which would disagree with the
 *    chain exactly when it matters.
 */
export const getTwyneUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const discovery = getCachedTwyneDiscovery(chainId, lender, account)
  const vaults = discovery?.vaults ?? []
  const expected = vaults.length * TWYNE_READS_PER_VAULT
  const markets = twyneChainData(lender, chainId)?.markets ?? []

  return [
    (data: any[]) => {
      if (expected === 0) return undefined
      if (!data || data.length < expected) return undefined

      // Group positions by market first: many vaults can share one market, and
      // each market produces ONE `UserData` with several sub-accounts.
      const byMarket = new Map<
        string,
        {
          collAddr: string
          debtAddr: string
          rows: { info: TwynePositionInfo; coll: bigint; debt: bigint }[]
        }
      >()

      vaults.forEach((ref, i) => {
        const base = i * TWYNE_READS_PER_VAULT
        const totalRaw = data[base]
        // A failed read is NOT an empty position — skipping keeps a transient
        // RPC failure from rendering as "you have nothing here".
        if (isFailedCall(totalRaw)) return

        const total = big(totalRaw)
        const reserved = isFailedCall(data[base + 1]) ? 0n : big(data[base + 1])
        const debt = isFailedCall(data[base + 2]) ? 0n : big(data[base + 2])
        const liqLtv = isFailedCall(data[base + 3]) ? 0n : big(data[base + 3])
        const canLiquidate = isFailedCall(data[base + 4]) ? false : Boolean(data[base + 4])
        const extLiquidated = isFailedCall(data[base + 5]) ? false : Boolean(data[base + 5])
        // `canRebalance()` REVERTS when there is nothing to release, so a
        // failed call here means zero rather than an error.
        const releasable = isFailedCall(data[base + 6]) ? 0n : big(data[base + 6])
        // Resolved on the DISCOVERY round — see `userCallBuild`. A missing one
        // means that read failed, and 1:1 is the only honest fallback: it is
        // the receipt's own floor, so the position is understated rather than
        // overstated.
        const scale = ref.collateralToUnderlying ?? WAD

        // Reserved credit belongs to the Credit-LPs; the borrower owns the rest.
        const userCollateral = total > reserved ? total - reserved : 0n
        if (userCollateral === 0n && debt === 0n) return

        const market = markets.find(
          (m) =>
            m.intermediateVault.toLowerCase() === ref.intermediateVault &&
            m.targetAsset.toLowerCase() === ref.targetAsset,
        )
        if (!market) return

        const lenderKey = twyneLenderKey(lender, chainId, ref.intermediateVault, ref.targetAsset)
        if (!byMarket.has(lenderKey)) {
          byMarket.set(lenderKey, {
            collAddr: market.underlyingAsset.toLowerCase(),
            debtAddr: market.targetAsset.toLowerCase(),
            rows: [],
          })
        }
        byMarket.get(lenderKey)!.rows.push({
          coll: (userCollateral * scale) / WAD,
          debt,
          info: {
            collateralVault: ref.vault,
            intermediateVault: ref.intermediateVault,
            totalAssetsDepositedOrReserved: total.toString(),
            reservedCredit: reserved.toString(),
            userCollateral: userCollateral.toString(),
            debt: debt.toString(),
            twyneLiqLtv: liqLtv.toString(),
            canLiquidate,
            isExternallyLiquidated: extLiquidated,
            releasableCredit: releasable.toString(),
            collateralToUnderlying: scale.toString(),
          },
        })
      })

      const out: { [lender: string]: UserData } = {}

      for (const [lenderKey, market] of byMarket) {
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) continue

        const collUid = createMarketUid(chainId, lenderKey, market.collAddr)
        const debtUid = createMarketUid(chainId, lenderKey, market.debtAddr)
        const collMeta = metaMap[collUid]
        const debtMeta = metaMap[debtUid]
        if (!collMeta && !debtMeta) continue

        const collDecimals = collMeta?.asset?.decimals ?? 18
        const debtDecimals = debtMeta?.asset?.decimals ?? 18
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
        const debtDisplay = debtMeta ? getDisplayPrice(debtMeta) : 0
        const debtOracle = debtMeta ? getOraclePrice(debtMeta) : 0
        const debtHist = debtMeta?.price?.priceUsd24h ?? debtDisplay

        const lendingPositions: Record<string, any> = {}
        const modes: Record<string, number> = {}
        const hist: Record<string, { totalDeposits24h: number; totalDebt24h: number }> = {}

        for (const row of market.rows) {
          // The sub-account IS the collateral vault address — a borrower can
          // hold several positions in one market, each with its own LTV.
          const id = row.info.collateralVault
          const collStr = parseRawAmount(row.coll.toString(), collDecimals)
          const collNum = Number(collStr)
          const debtStr = parseRawAmount(row.debt.toString(), debtDecimals)
          const debtNum = Number(debtStr)

          lendingPositions[id] = {
            [collUid]: {
              marketUid: collUid,
              underlying: market.collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
              twyneInfo: row.info,
            },
            [debtUid]: {
              marketUid: debtUid,
              underlying: market.debtAddr,
              deposits: '0',
              debt: debtStr,
              debtStable: '0',
              depositsUSD: 0,
              debtUSD: debtNum * debtDisplay,
              debtStableUSD: 0,
              depositsUSDOracle: 0,
              debtUSDOracle: debtNum * debtOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
              twyneInfo: row.info,
            },
          }
          // THE VALUE HALF of the parameter model. `modes[posId]` carries this
          // position's CHOSEN liquidation LTV as a fraction, which the market's
          // `config[0].openParameter` declares the domain for — and because that
          // parameter is an IDENTITY mapping, `resolveModeConfig` prices the
          // position straight from it. Leaving this at 0 would price every
          // position at the band's FLOOR, i.e. as if it had bought no extra LTV.
          modes[id] = Number(row.info.twyneLiqLtv) / 10_000
          hist[id] = {
            totalDeposits24h: collNum * collHist,
            totalDebt24h: debtNum * debtHist,
          }
        }

        if (Object.keys(lendingPositions).length === 0) continue

        const userData = createMultiAccountTypeUserState(
          { chainId, account, lendingPositions, modes } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
